import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { History as HistoryIcon, Download, CheckSquare, Square, RotateCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { listJobs, listTrucks, api, archiveJob, unarchiveJob, archiveJobsBulk, setBillingState, restoreCancelledJob } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isAdmin as isAdminUser, isDispatcher as isDispatcherUser, can } from "../lib/roles";
import { cn } from "../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import EditJobSheet from "../components/EditJobSheet";
import JobDetailSheet from "../components/JobDetailSheet";
import { SERVICE_LABEL } from "../lib/constants";
import { toast } from "sonner";

import HistoryFilters from "../components/history/HistoryFilters";
import HistoryBulkBar from "../components/history/HistoryBulkBar";
import HistoryRow from "../components/history/HistoryRow";
import SignToNtsDialog from "../components/SignToNtsDialog";
import { tenantDayStart, tenantTodayIso } from "../lib/tenantTime";

// Converts a UI range key into an ISO `since` timestamp for the API.
// Returns null for "all" (no lower bound).
function rangeToSince(range) {
  if (range === "all") return null;
  const daysAgo = { yesterday: 1, "7d": 7, "30d": 30 }[range] || 0;
  return tenantDayStart(daysAgo).toISOString();
}

// Used only for "yesterday" — caps results at midnight today.
function rangeToUntil(range) {
  if (range !== "yesterday") return null;
  return tenantDayStart(0).toISOString();
}

// Source bucketer — mirrors the Dashboard "Source Breakdown" widget so a
// drill-down chip filters the same way the widget counts.
const bucketOf = (s) => {
  if (s === "aa_workforms" || s === "aa_sms") return "aa";
  if (s === "servicenow_api" || s === "servicenow_image" || s === "external_feed") return "sn";
  if (s === "manual") return "manual";
  return "other";
};

const BILLING_LABELS = {
  to_invoice: "To Invoice",
  invoice_sent: "Invoice Sent",
  non_chargeable: "Non Chargeable",
  written_off: "Written Off",
};

export default function HistoryPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  // Feb 2026 (Commit D) — helpers accept `owner` as also satisfying
  // admin/dispatcher/viewer. Existing NTS role-specific behaviour
  // preserved because helpers return true for the exact role match too.
  const isAdmin = isAdminUser(user);
  const isDispatcher = isDispatcherUser(user);
  // Sep 2026 — default range for all back-office users is "All Completed"
  // so searches (rego, customer name, etc.) surface older jobs by default.
  const defaultRange = "all";

  const initialQ = params.get("q") || "";
  const initialSource = params.get("source") || "";
  // Feb 2026: "Archived" is now a first-class range option (unified with the
  // date dropdown). Backwards-compat: an inbound `?archived=1` URL param
  // (from the old standalone toggle) still routes into the archived view.
  const initialRange = params.get("range")
    || (params.get("archived") === "1" ? "archived" : defaultRange);

  const [query, setQuery] = useState(initialQ);
  const [range, setRange] = useState(initialRange);
  const [sourceFilter, setSourceFilter] = useState(initialSource);
  // Derived — range dropdown is the single source of truth.
  const archivedView = range === "archived";

  // Dispatcher gets READ-ONLY access to archived jobs so they can look up a
  // billing-invoiced job for a customer question without accidentally
  // mutating the record. Admin keeps full manage actions.
  const dispatcherReadOnly = isDispatcher && archivedView;

  // Jun 2026 — per-user switches. Each flag is the OLD role rule AND the
  // owner's switch, so a switch can only ever take a button away, never
  // hand someone a button this screen never gave them.
  const canDelete = isAdmin && can(user, "delete_jobs");  // hard delete = admin only
  const canEdit = (isAdmin || isDispatcher) && !dispatcherReadOnly;
  // Archive workflow (post-invoice archive flow). Sep 2026 — viewer
  // retired; dispatcher absorbed archive capability.
  const canArchive = (isAdmin || (isDispatcher && !dispatcherReadOnly))
    && can(user, "archive_jobs");
  // Marking a job invoiced / non-chargeable / written off.
  const canSetBilling = (isAdmin || isDispatcher) && !dispatcherReadOnly
    && can(user, "set_billing_state");
  // Sign abandoned vehicles over to NTS in lieu.
  const canSignToNts = (isAdmin || isDispatcher) && !dispatcherReadOnly
    && can(user, "sign_over_vehicles");
  // TA# is editable by any back-office user.
  const canSetTowAuthority = (isAdmin || isDispatcher) && !dispatcherReadOnly;
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [archivingBulk, setArchivingBulk] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [signToNtsJob, setSignToNtsJob] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  // Set of jobIds currently mid-invoice-toggle. Used to disable double-clicks
  // and apply a wait cursor during the optimistic update window.
  const [togglingInvoiced, setTogglingInvoiced] = useState(() => new Set());
  // Visual feedback when the user taps the manual refresh button — the
  // icon spins briefly so they get confirmation that something happened.
  const [manualRefreshing, setManualRefreshing] = useState(false);

  const truckById = useMemo(() => {
    const m = {};
    trucks.forEach((t) => { m[t.id] = t; });
    return m;
  }, [trucks]);

  // Request-ID guard: stale responses from earlier searches can't clobber
  // fresh state. Prevents "typed rego then list snaps back to unfiltered"
  // when a slower earlier request wins the network race (Aug 2026 sweep).
  const reqIdRef = useRef(0);

  const refresh = useCallback(async ({ background = false } = {}) => {
    const myReqId = ++reqIdRef.current;
    // Background polls/focus refreshes must NOT flip `loading`: doing so
    // unmounts the whole row list for a "Loading…" placeholder, which
    // collapses the table and snaps the page back to the top mid-scroll.
    // Only the initial load and the manual Refresh button show the skeleton.
    if (!background) setLoading(true);
    try {
      const trimmedQ = query.trim();
      // Feb 2026: previously we forced ALL-completed whenever the user
      // typed a query (to avoid accidentally hiding an invoicing hit).
      // But that also *locked* the dropdown, so widening a rego search
      // meant deleting the rego, changing the range, then retyping. Now
      // the range dropdown always applies — user picks "All Completed"
      // explicitly when a narrow window comes back empty.
      const dateActive = !archivedView;
      const sinceISO = dateActive ? rangeToSince(range) : null;
      const untilISO = dateActive ? rangeToUntil(range) : null;
      const reqParams = { status: "completed,cancelled" };
      // Sep 2026 — viewer retired; unified sort order (newest first)
      // for every back-office user.
      if (sinceISO) reqParams.since = sinceISO;
      if (trimmedQ) reqParams.q = trimmedQ;
      // Archive view → fetch ONLY archived. Otherwise backend default
      // (excludes archived) applies.
      if (archivedView) reqParams.archived_only = true;
      const [j, t] = await Promise.all([
        listJobs(reqParams),
        listTrucks().catch(() => []),
      ]);
      // Stale-response drop.
      if (myReqId !== reqIdRef.current) return;
      let filtered = j;
      if (untilISO) {
        // Compare via Date.parse so a row with a malformed/legacy timestamp
        // (no TZ designator, space separator) is still bucketed by its real
        // wall-clock instant rather than by lexical string order. Without
        // this, "2026-06-27 22:59:43" (a TODAY job in NZ) lexically comes
        // BEFORE "2026-06-27T12:00:00Z" (NZ midnight) and slipped into the
        // Yesterday tab.
        const untilMs = Date.parse(untilISO);
        filtered = filtered.filter((x) => {
          const ts = x.completed_at || x.updated_at || x.created_at;
          if (!ts) return false;
          const ms = Date.parse(ts);
          return Number.isFinite(ms) && ms < untilMs;
        });
      }
      // Sort newest-first for every back-office user.
      filtered.sort((a, b) => {
        const ta = a.completed_at || a.updated_at || a.created_at || "";
        const tb = b.completed_at || b.updated_at || b.created_at || "";
        return tb.localeCompare(ta);
      });
      if (sourceFilter) {
        filtered = filtered.filter((x) => bucketOf(x.source) === sourceFilter);
      }
      setJobs(filtered);
      setTrucks(t);
      // Reset bulk selection only on a foreground (re)load; a silent
      // background poll keeps the user's current selection intact.
      if (!background) setSelectedIds(new Set());
    } catch {
      if (myReqId !== reqIdRef.current) return;
      toast.error("Failed to load history");
    } finally {
      if (myReqId === reqIdRef.current && !background) setLoading(false);
    }
  }, [range, query, sourceFilter, archivedView]);

  useEffect(() => { refresh(); }, [refresh]);

  // Background poll while the tab is visible. Pauses when the user tabs away
  // so we're not hammering the server overnight, then snaps back (via the
  // visibilitychange listener below) the moment they refocus. 120s keeps an
  // all-day History tab cheap; background polls are silent (no "Loading…"
  // flash, no scroll jump) so a dispatcher scrolling the list isn't yanked
  // back to the top mid-refresh.
  useEffect(() => {
    const POLL_MS = 120000;
    const tick = () => { if (!document.hidden) refresh({ background: true }); };
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const manualRefresh = async () => {
    setManualRefreshing(true);
    try {
      await refresh();
    } finally {
      // Hold the spin briefly even on a fast network so the user sees feedback.
      setTimeout(() => setManualRefreshing(false), 300);
    }
  };

  // Sync URL when local filter state changes (but omit role-default values
  // so a viewer's URL doesn't carry their own default range).
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (query) next.set("q", query); else next.delete("q");
    if (range !== defaultRange) next.set("range", range); else next.delete("range");
    if (sourceFilter) next.set("source", sourceFilter); else next.delete("source");
    // Legacy `?archived=1` param is now folded into `range=archived`.
    next.delete("archived");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [query, range, sourceFilter, defaultRange, params, setParams]);

  // Topbar global search broadcasts here.
  useEffect(() => {
    const onSearch = (e) => setQuery(e.detail?.q ?? "");
    window.addEventListener("global-search", onSearch);
    return () => window.removeEventListener("global-search", onSearch);
  }, []);

  // Refresh on tab focus so the billing user picks up dispatcher edits made while she
  // was on another tab — avoids polling but keeps her data fresh.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) refresh({ background: true }); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  // ─── Row-level callbacks (lifted so optimistic updates stay centralised) ─

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/jobs/${confirmDelete.id}`);
      toast.success("Job deleted from history");
      setJobs((js) => js.filter((j) => j.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const doArchive = async (job) => {
    try {
      await archiveJob(job.id);
      toast.success(`Archived ${job.customer_name || "job"}`);
      setJobs((js) => js.filter((j) => j.id !== job.id));
      setSelectedIds((s) => {
        const n = new Set(s);
        n.delete(job.id);
        return n;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to archive");
    }
  };

  const doUnarchive = async (job) => {
    try {
      await unarchiveJob(job.id);
      toast.success(`Restored ${job.customer_name || "job"}`);
      setJobs((js) => js.filter((j) => j.id !== job.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unarchive");
    }
  };

  // Restore a cancelled job back into the pending dispatch queue —
  // "customer cancelled, then rang back later" without re-keying the job.
  const doRestore = async (job) => {
    try {
      await restoreCancelledJob(job.id);
      toast.success(`${job.customer_name || "Job"} restored to the dispatch queue`);
      setJobs((js) => js.filter((j) => j.id !== job.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore job");
    }
  };

  // Optimistic billing-state change with server-side rollback on error.
  const doSetBillingState = async (job, state) => {
    if (togglingInvoiced.has(job.id) || job.billing_state === state) return;
    const previous = job.billing_state;
    setTogglingInvoiced((s) => new Set(s).add(job.id));
    setJobs((js) => js.map((j) => (j.id === job.id ? { ...j, billing_state: state } : j)));
    try {
      const updated = await setBillingState(job.id, state);
      setJobs((js) => js.map((j) => (j.id === updated.id ? { ...j, ...updated } : j)));
      toast.success(`Marked ${BILLING_LABELS[state] || state}`);
    } catch (e) {
      setJobs((js) => js.map((j) => (j.id === job.id ? { ...j, billing_state: previous } : j)));
      toast.error(e.response?.data?.detail || "Couldn't update the billing state");
    } finally {
      setTogglingInvoiced((s) => {
        const n = new Set(s);
        n.delete(job.id);
        return n;
      });
    }
  };

  const doArchiveSelected = async () => {
    if (selectedIds.size === 0) return;
    setArchivingBulk(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await archiveJobsBulk(ids);
      toast.success(`Archived ${res.archived} job${res.archived === 1 ? "" : "s"}`);
      setJobs((js) => js.filter((j) => !selectedIds.has(j.id)));
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk archive failed");
    } finally {
      setArchivingBulk(false);
    }
  };

  const toggleRowSelected = (jobId) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(jobId)) n.delete(jobId); else n.add(jobId);
      return n;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((s) => {
      if (s.size === jobs.length) return new Set();
      return new Set(jobs.map((j) => j.id));
    });
  };

  const onTowAuthoritySaved = useCallback((updated) => {
    setJobs((js) => js.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
  }, []);

  const completedCount = jobs.filter((j) => j.status === "completed").length;
  const cancelledCount = jobs.filter((j) => j.status === "cancelled").length;

  // Total `<th>`/`<td>` count for the loading + empty-state colspan.
  const totalCols = (canArchive && !archivedView ? 1 : 0)  // checkbox
    + 8                                                    // When, Status, Customer, Vehicle, Service, Truck, RoundTrip, Reference
    + (canSetTowAuthority ? 1 : 0)
    + (canSetBilling ? 1 : 0)                              // Invoice
    + ((canEdit || canDelete || canArchive || canSignToNts) ? 1 : 0);  // actions

  const exportCsv = () => {
    if (jobs.length === 0) { toast.error("Nothing to export"); return; }
    const rows = [
      ["When", "Status", "Customer", "Phone", "Vehicle", "Rego", "Service", "Pickup", "Drop-off", "Round Trip (km)", "Truck", "WO #", "AA Job ID", "Tow Auth #", "Notes"],
      ...jobs.map((j) => [
        j.completed_at || j.updated_at || j.created_at,
        j.status,
        j.customer_name,
        j.customer_phone,
        j.vehicle,
        j.rego,
        SERVICE_LABEL[j.service_type] || j.service_type,
        j.address,
        j.drop_off_address || "",
        j.round_trip_km != null ? j.round_trip_km : "",
        truckById[j.assigned_truck_id]?.name || "",
        j.work_order_number || "",
        j.aa_job_id || "",
        j.tow_authority_number || "",
        (j.notes || "").replace(/\n/g, " | "),
      ]),
    ];
    const csv = rows
      .map((r) =>
        r.map((cell) => {
          const s = String(cell ?? "");
          return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dispatch-history-${range}-${tenantTodayIso()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-4" data-testid="history-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
            <HistoryIcon className="h-7 w-7 text-blue-600" />
            History
          </h1>
          <p className="text-sm text-slate-500 mt-1">Completed and cancelled jobs. Search by customer, plate, address, WO# or notes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={manualRefresh}
            disabled={manualRefreshing || loading}
            title="Refresh now (auto-polls every 60s)"
            data-testid="history-refresh-btn"
          >
            <RotateCw className={cn("h-4 w-4 mr-1.5", (manualRefreshing || loading) && "animate-spin")} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={jobs.length === 0}
            data-testid="history-export-csv-btn"
          >
            <Download className="h-4 w-4.5" /> Export CSV
          </Button>
        </div>
      </div>

      <HistoryFilters
        query={query} setQuery={setQuery}
        range={range} setRange={setRange}
        sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
        loading={loading}
        completedCount={completedCount}
        cancelledCount={cancelledCount}
      />

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        {canArchive && !archivedView && (
          <HistoryBulkBar
            selectedCount={selectedIds.size}
            onClear={() => setSelectedIds(new Set())}
            onArchiveSelected={doArchiveSelected}
            archivingBulk={archivingBulk}
          />
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr>
              {canArchive && !archivedView && (
                <th className="px-2 py-2 w-8">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-slate-400 hover:text-violet-600 transition-colors"
                    title={selectedIds.size === jobs.length && jobs.length > 0 ? "Deselect all" : "Select all"}
                    data-testid="history-select-all-toggle"
                  >
                    {selectedIds.size > 0 && selectedIds.size === jobs.length ? (
                      <CheckSquare className="h-4 w-4 text-violet-600" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </th>
              )}
              <th className="text-left px-2 py-2 font-bold">When</th>
              <th className="text-left px-2 py-2 font-bold">Status</th>
              <th className="text-left px-2 py-2 font-bold">Customer</th>
              <th className="text-left px-2 py-2 font-bold">Vehicle</th>
              <th className="text-left px-2 py-2 font-bold">Service</th>
              <th className="text-left px-2 py-2 font-bold">Truck</th>
              <th className="text-right px-2 py-2 font-bold whitespace-nowrap">Round Trip</th>
              <th className="text-left px-2 py-2 font-bold">Reference</th>
              {canSetTowAuthority && <th className="text-left px-2 py-2 font-bold whitespace-nowrap">Tow Authority #</th>}
              {canSetBilling && <th className="text-left px-2 py-2 font-bold whitespace-nowrap">Invoice</th>}
              {(canEdit || canDelete || canArchive || canSignToNts) && <th className="px-2 py-2"></th>}
            </tr>
          </thead>
          <tbody data-testid="history-rows">
            {loading && (
              <tr><td colSpan={totalCols} className="text-center text-slate-500 py-12">Loading…</td></tr>
            )}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={totalCols} className="text-center text-slate-400 py-12">
                {archivedView ? "No archived jobs in this range." : "No matching jobs in this range."}
              </td></tr>
            )}
            {!loading && jobs.map((j) => (
              <HistoryRow
                key={j.id}
                job={j}
                truck={truckById[j.assigned_truck_id]}
                isSelected={selectedIds.has(j.id)}
                isAdmin={isAdmin}
                archivedView={archivedView}
                canEdit={canEdit}
                canDelete={canDelete}
                canArchive={canArchive}
                canSetBilling={canSetBilling}
                canSignToNts={canSignToNts}
                canSetTowAuthority={canSetTowAuthority}
                canRestore={canEdit && !archivedView}
                togglingInvoiced={togglingInvoiced.has(j.id)}
                onClick={() => setDetailJob(j)}
                onSelect={() => toggleRowSelected(j.id)}
                onEdit={setEditJob}
                onArchive={doArchive}
                onUnarchive={doUnarchive}
                onDelete={setConfirmDelete}
                onSetBillingState={doSetBillingState}
                onTowAuthoritySaved={onTowAuthoritySaved}
                onSignToNts={setSignToNtsJob}
                onRestore={doRestore}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="history-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black">Delete this job from history?</DialogTitle>
            <DialogDescription>
              This permanently removes <strong>{confirmDelete?.customer_name}</strong> · {confirmDelete?.vehicle} from the database. The action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={doDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="history-delete-confirm-btn"
            >
              {deleting ? "Deleting…" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditJobSheet
        job={editJob}
        open={!!editJob}
        onOpenChange={(o) => !o && setEditJob(null)}
        onSaved={(updated) => {
          // Optimistic local merge for instant feedback, then re-fetch to pick
          // up backend-side adjustments (geocoding, round_trip_km recompute).
          setJobs((js) => js.map((j) => (j.id === updated.id ? { ...j, ...updated } : j)));
          setEditJob(null);
          refresh();
        }}
      />

      <JobDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        truckById={truckById}
      />

      <SignToNtsDialog
        job={signToNtsJob}
        open={!!signToNtsJob}
        onOpenChange={(o) => !o && setSignToNtsJob(null)}
        onDone={() => { setSignToNtsJob(null); refresh(); }}
      />
    </div>
  );
}
